// Training Yard — an outdoor CQB training compound.
// Concrete perimeter, target dummies, sandbags, crates, ammo drums, a small
// range office with windows, and a watchtower. Suitable for 1v1–2v2 duels.
import * as THREE from "three";
import EnvironmentAtmosphere from "@/game/systems/Environment";
import {
    ConcreteWall,
    Crate,
    Barrel,
    Sandbags,
    Fence,
    TargetDummy,
    Sign,
    Building,
    Ground,
    TrainingFloor,
    StreetLamp,
} from "@/game/systems/props";
import { MAT } from "@/game/systems/materials";

// Collision AABBs — keep in sync with visible cover
export const TRAINING_YARD_WALLS = [
    // Perimeter (concrete)
    { min: [-25, 0, -25], max: [-24, 4, 25] },
    { min: [24, 0, -25], max: [25, 4, 25] },
    { min: [-25, 0, -25], max: [25, 4, -24] },
    { min: [-25, 0, 24], max: [25, 4, 25] },

    // Crates & sandbags
    { min: [-5, 0, -6], max: [-2, 1.6, -3] },
    { min: [3, 0, -8], max: [6, 1.6, -5] },
    { min: [-10, 0, 2], max: [-7, 1.6, 5] },
    { min: [7, 0, 3], max: [10, 1.6, 6] },

    // Center sandbag bunker
    { min: [-2.5, 0, 8], max: [2.5, 1.3, 9.5] },

    // Range office (small building at north edge)
    { min: [-6, 0, -19], max: [6, 4, -14] },

    // Watchtower base
    { min: [17, 0, -17], max: [20, 5, -14] },

    // Half-walls providing angles
    { min: [12, 0, -2], max: [16, 2.6, 0] },
    { min: [-16, 0, -1], max: [-12, 2.6, 1] },
    { min: [-6, 0, 14], max: [-2, 2.6, 16] },
    { min: [10, 0, 12], max: [14, 2.6, 14] },
];

export const TRAINING_YARD_SPAWNS = {
    player: { position: [0, 1.7, 20], yaw: Math.PI },
    enemies: [
        [0, 1.7, -20],
        [-8, 1.7, -18],
        [8, 1.7, -18],
        [-14, 1.7, -14],
        [14, 1.7, -14],
    ],
    allies: [
        [-4, 1.7, 20],
        [4, 1.7, 20],
        [-8, 1.7, 20],
        [8, 1.7, 20],
    ],
};

export const TRAINING_YARD_WAYPOINTS = [
    [0, 0, -12], [-8, 0, -10], [8, 0, -10],
    [-14, 0, -6], [14, 0, -6],
    [-6, 0, 0], [6, 0, 0], [0, 0, 6],
    [-10, 0, 10], [10, 0, 10],
];

export const TRAINING_YARD_META = {
    id: "training_yard",
    name: "Training Yard",
    walls: TRAINING_YARD_WALLS,
    spawns: TRAINING_YARD_SPAWNS,
    waypoints: TRAINING_YARD_WAYPOINTS,
    bounds: { minX: -24, maxX: 24, minZ: -24, maxZ: 24 },
    // Preview metadata for MapPicker cards
    preview: {
        environment: "OUTDOOR COMPOUND",
        recommendedPlayers: "2 – 4",
        difficulty: "BEGINNER",
        palette: ["#8b8b83", "#7d3b2a", "#3a3c40"],
        tagline: "Practice grounds with clean sightlines and simple cover.",
    },
};

// --- Rendered map ---
export default function TrainingYard({ shadows, timeOfDay = "day", weather = "sunny" }) {
    return (
        <group>
            <EnvironmentAtmosphere timeOfDay={timeOfDay} weather={weather} shadows={shadows} />

            {/* Floor */}
            <TrainingFloor size={60} />

            {/* Concrete perimeter — replaces raw box walls */}
            <ConcreteWall position={[-24.5, 2, 0]} size={[1, 4, 50]} shadows={shadows} />
            <ConcreteWall position={[24.5, 2, 0]} size={[1, 4, 50]} shadows={shadows} />
            <ConcreteWall position={[0, 2, -24.5]} size={[50, 4, 1]} shadows={shadows} />
            <ConcreteWall position={[0, 2, 24.5]} size={[50, 4, 1]} shadows={shadows} />

            {/* Range office at north */}
            <Building
                position={[0, 2, -16.5]}
                size={[12, 4, 5]}
                color="concreteLight"
                withWindows
                shadows={shadows}
            />
            {/* Range office door light */}
            <pointLight position={[0, 3.5, -13.5]} intensity={0.6} color="#ffb26b" distance={8} decay={2} />

            {/* Watchtower */}
            <group position={[18.5, 0, -15.5]}>
                {[[-1.4, -1.4], [1.4, -1.4], [-1.4, 1.4], [1.4, 1.4]].map(([x, z], i) => (
                    <mesh key={i} position={[x, 2.5, z]} castShadow={shadows} material={MAT.wood()}>
                        <boxGeometry args={[0.2, 5, 0.2]} />
                    </mesh>
                ))}
                {/* Deck */}
                <mesh position={[0, 5, 0]} material={MAT.woodLight()}>
                    <boxGeometry args={[3.2, 0.2, 3.2]} />
                </mesh>
                {/* Roof */}
                <mesh position={[0, 7.2, 0]} rotation={[0, Math.PI / 4, 0]} material={MAT.metalDark()}>
                    <coneGeometry args={[2.4, 1.5, 4]} />
                </mesh>
                {/* Beacon */}
                <pointLight position={[0, 6.5, 0]} intensity={0.8} color="#ff5040" distance={12} />
            </group>

            {/* Crates */}
            <Crate position={[-3.5, 0.7, -4.5]} shadows={shadows} />
            <Crate position={[4.5, 0.7, -6.5]} rotation={[0, 0.3, 0]} shadows={shadows} />
            <Crate position={[-8.5, 0.7, 3.5]} shadows={shadows} />
            <Crate position={[8.5, 0.7, 4.5]} shadows={shadows} />

            {/* Sandbag bunker in center */}
            <Sandbags position={[0, 0, 8.5]} length={5} rows={3} shadows={shadows} />

            {/* Half-walls */}
            <ConcreteWall position={[14, 1.3, -1]} size={[4, 2.6, 2]} stripe={false} shadows={shadows} />
            <ConcreteWall position={[-14, 1.3, 0]} size={[4, 2.6, 2]} stripe={false} shadows={shadows} />
            <ConcreteWall position={[-4, 1.3, 15]} size={[4, 2.6, 2]} stripe={false} shadows={shadows} />
            <ConcreteWall position={[12, 1.3, 13]} size={[4, 2.6, 2]} stripe={false} shadows={shadows} />

            {/* Target dummies along the range */}
            {[-18, -14, -10].map((z, i) => (
                <TargetDummy key={i} position={[-18 + i * 5, 0, z]} rotation={[0, Math.PI, 0]} />
            ))}

            {/* Barrels */}
            <Barrel position={[-11, 0.55, -8]} color="metalRed" shadows={shadows} />
            <Barrel position={[-10.5, 0.55, -7.5]} color="metalYellow" shadows={shadows} />
            <Barrel position={[11, 0.55, -8]} color="metalRust" shadows={shadows} />

            {/* Fences dividing lanes */}
            <Fence position={[-18, 0, 4]} rotation={[0, Math.PI / 2, 0]} length={5} shadows={shadows} />
            <Fence position={[18, 0, 4]} rotation={[0, Math.PI / 2, 0]} length={5} shadows={shadows} />

            {/* Signs */}
            <Sign position={[-8, 0, 18]} color="signBlue" />
            <Sign position={[8, 0, 18]} rotation={[0, -0.3, 0]} color="signBlue" />

            {/* Corner lamps */}
            <StreetLamp position={[-22, 0, -22]} shadows={shadows} />
            <StreetLamp position={[22, 0, -22]} shadows={shadows} />
            <StreetLamp position={[-22, 0, 22]} shadows={shadows} />
            <StreetLamp position={[22, 0, 22]} shadows={shadows} />

            {/* Grass strip outside compound (visible over the wall in daylight) */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow material={MAT.grassDry()}>
                <planeGeometry args={[120, 120]} />
            </mesh>
        </group>
    );
}
